'use client';

import { CategoryPageSideFilters } from '@c/CategoryOrSubcategoryPage';
import { slugify } from '@util/urlHelpers';
import { CategoryDocument } from 'models/category';
import { FacetCounts } from 'models/product';
import { Gender, RideType } from 'models/shared';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { isMobile } from '..';
import { PageSideFilters } from './useProductsPage';

type CategoryFacetCount = {
  value: string;
  count: number;
  order: string;
};

export interface FormattedFacetCount {
  category?: CategoryFacetCount;
  value: string;
  count: number;
  order: string;
  // subcats: {
  //   cat1?: FacetCountValue;
  //   cat2?: FacetCountValue;
  // };
}

export const useCategoryFilters = ({
  filterState,
  setFilterState,
  currentCat,
  allBrands,
  facet_counts,
  categories,
}: {
  filterState: CategoryPageSideFilters;
  setFilterState: (filterState: CategoryPageSideFilters) => void;
  currentCat: CategoryDocument | null | undefined;
  allBrands: string[] | undefined;
  facet_counts: FacetCounts | undefined;
  categories: CategoryDocument[];
}) => {
  const [brandFilter, setBrandFilter] = useState<string>('');
  const [aspectRatioFilter, setAspectRatioFilter] = useState<string>('');
  const pathname = usePathname();
  const [startAllClosed, setStartAllClosed] = useState(false);
  const [showMobileDrawer, setShowMobileDrawer] = useState(false);

  // pending filters are the accumulated changes from the mobile filters, they are all applied at once by the applyPendingFilterChanges function
  // we only use the pending filters on mobile because the desktop doesn't have an apply button
  const [pendingFilterChanges, setPendingFilterChanges] = useState<
    PageSideFilters | undefined
  >(filterState);

  const handlePricesChange = (min: number | null, max: number | null) => {
    if (isMobile()) {
      setPendingFilterChanges((prev) => ({
        ...filterState,
        ...prev,
        minPrice: min,
        maxPrice: max,
      }));
    } else {
      setFilterState({
        ...filterState,
        minPrice: min,
        maxPrice: max,
      });
    }
  };

  const handleBrandsChange = (filters: string[]) => {
    if (isMobile()) {
      setPendingFilterChanges((prev) => ({
        ...filterState,
        ...prev,
        brands: filters,
      }));
    } else {
      setFilterState({
        ...filterState,
        brands: filters,
      });
    }
  };

  const handleSaleChange = (onsale: boolean) => {
    const newFilters = {
      onsale,
    };

    if (isMobile()) {
      setPendingFilterChanges({
        ...filterState,
        ...pendingFilterChanges,
        ...newFilters,
      });
    } else {
      setFilterState({
        ...filterState,
        ...newFilters,
      });
    }
  };
  const handleRecentlySoldChange = (recentlySold: boolean) => {
    const newFilters = {
      ...filterState,
      recentlySold,
    };

    if (isMobile()) {
      const newPendingFilterChanges = {
        ...pendingFilterChanges,
        ...newFilters,
      };

      setPendingFilterChanges(newPendingFilterChanges);
    } else {
      setFilterState(newFilters);
    }
  };
  const handleGenderChange = (filters: string[]) => {
    if (isMobile()) {
      setPendingFilterChanges((prev) => ({
        ...filterState,
        ...prev,
        genders: filters as Gender[],
      }));
    } else {
      setFilterState({
        ...filterState,
        genders: filters as Gender[],
      });
    }
  };
  const handleLetterSizeChange = (filters: string[]) => {
    if (isMobile()) {
      setPendingFilterChanges((prev) => ({
        ...filterState,
        ...prev,
        letterSizes: filters,
      }));
    } else {
      setFilterState({
        ...filterState,
        letterSizes: filters,
      });
    }
  };

  const handleAspectRatioChange = (filters: string[]) => {
    if (isMobile()) {
      setPendingFilterChanges((prev) => ({
        ...filterState,
        ...prev,
        aspectRatios: filters,
      }));
    } else {
      setFilterState({
        ...filterState,
        aspectRatios: filters,
      });
    }
  };

  const handleRimDiameterChange = (filters: string[]) => {
    if (isMobile()) {
      setPendingFilterChanges((prev) => ({
        ...filterState,
        ...prev,
        rimDiameters: filters,
      }));
    } else {
      setFilterState({
        ...filterState,
        rimDiameters: filters,
      });
    }
  };

  const handleMountingPositionChange = (filters: string[]) => {
    if (isMobile()) {
      setPendingFilterChanges((prev) => ({
        ...filterState,
        ...prev,
        mountingPositions: filters,
      }));
    } else {
      setFilterState({
        ...filterState,
        mountingPositions: filters,
      });
    }
  };

  const handleTireDiameterChange = (filters: string[]) => {
    if (isMobile()) {
      setPendingFilterChanges((prev) => ({
        ...filterState,
        ...prev,
        tireDiameters: filters,
      }));
    } else {
      setFilterState({
        ...filterState,
        tireDiameters: filters,
      });
    }
  };

  const handleWidthChange = (filters: string[]) => {
    if (isMobile()) {
      setPendingFilterChanges((prev) => ({
        ...filterState,
        ...prev,
        widths: filters,
      }));
    } else {
      setFilterState({
        ...filterState,
        widths: filters,
      });
    }
  };

  const handleConstructionChange = (filters: string[]) => {
    if (isMobile()) {
      setPendingFilterChanges((prev) => ({
        ...filterState,
        ...prev,
        construction: filters,
      }));
    } else {
      setFilterState({
        ...filterState,
        construction: filters,
      });
    }
  };

  const handleNumberSizeChange = (filters: string[]) => {
    if (isMobile()) {
      setPendingFilterChanges((prev) => ({
        ...filterState,
        ...prev,
        numberSizes: filters,
      }));
    } else {
      setFilterState({
        ...filterState,
        numberSizes: filters,
      });
    }
  };

  const handleShipToChange = (filters: string[]) => {
    if (isMobile()) {
      setPendingFilterChanges((prev) => ({
        ...filterState,
        ...prev,
        shipTo: filters,
      }));
    } else {
      setFilterState({
        ...filterState,
        shipTo: filters,
      });
    }
  };
  const handleShipFromChange = (filters: string[]) => {
    if (isMobile()) {
      setPendingFilterChanges((prev) => ({
        ...filterState,
        ...prev,
        shipFrom: filters,
      }));
    } else {
      setFilterState({
        ...filterState,
        shipFrom: filters,
      });
    }
  };
  const handleCategoryChange = (
    product_cat: string,
    level: 'ride_type' | 'category' | 'category1' | 'category2' | 'category3'
  ) => {
    const newFilters = {
      ...filterState,
      product_cats: {
        ...filterState.product_cats,
        [level]: product_cat,
      },
    };

    if (isMobile()) {
      const newPendingFilterChanges = {
        ...pendingFilterChanges,
        ...newFilters,
      };

      setPendingFilterChanges(newPendingFilterChanges);
    } else {
      setFilterState(newFilters);
    }
  };
  const handleRideTypeChange = (filters: RideType[]) => {
    if (isMobile()) {
      setPendingFilterChanges((prev) => ({
        ...filterState,
        ...prev,
        rideTypes: filters,
      }));
    } else {
      setFilterState({
        ...filterState,
        rideTypes: filters,
      });
    }
  };
  const handleFreeShippingChange = (is_flat_rate: boolean) => {
    const newFilters = {
      ...filterState,
      is_flat_rate,
    };

    if (isMobile()) {
      const newPendingFilterChanges = {
        ...pendingFilterChanges,
        ...newFilters,
      };

      setPendingFilterChanges(newPendingFilterChanges);
    } else {
      setFilterState(newFilters);
    }
  };

  const handleConditionChange = (filters: ('New' | 'Used')[]) => {
    if (isMobile()) {
      setPendingFilterChanges((prev) => ({
        ...filterState,
        ...prev,
        conditions: filters,
      }));
    } else {
      setFilterState({
        ...filterState,
        conditions: filters,
      });
    }
  };
  function applyPendingFilterChanges() {
    if (pendingFilterChanges) {
      setFilterState(pendingFilterChanges);
      setPendingFilterChanges(undefined);
    }
  }

  function getSizeTitle() {
    if (currentCat) {
      if (
        currentCat.name === 'Gear' ||
        currentCat.name === 'Gear Combos' ||
        currentCat.name === 'Jerseys'
      ) {
        return 'Jersey Size';
      } else if (currentCat.name === 'Helmets') {
        return 'Helmet Size';
      }
    }
    return 'Size';
  }

  const getBrandFilterOptions = () => {
    const brandFacets = facet_counts?.brand ?? [];
    if (brandFilter || filterState.brands.length) {
      const allBrandsAndFacets = allBrands
        ?.concat(brandFacets.map((b) => b.value))
        .concat(filterState.brands ?? []);
      const setBrandAndFacets = new Set(allBrandsAndFacets);
      const arrayBrandsAndFacets = Array.from(setBrandAndFacets).sort((a, b) =>
        filterState.brands.includes(a) ? -1 : a.localeCompare(b)
      );
      return arrayBrandsAndFacets
        ?.filter((b) => b.toLowerCase().includes(brandFilter.toLowerCase()))
        .map((b) => ({ key: b, label: b }));
    } else {
      return brandFacets.map((b) => ({ key: b.value, label: b.value }));
    }
  };

  let display = '';
  const cat = categories[0];
  if (cat) {
    if (cat.product_cats.ride_type && cat.cat_level > 0) {
      display += `${cat.product_cats.ride_type} `;
    }
    if (cat.product_cats.category && cat.cat_level > 1) {
      display += `> ${cat.product_cats.category} `;
    }
    if (cat.product_cats.category1 && cat.cat_level > 2) {
      display += `> ${cat.product_cats.category1} `;
    }
    if (cat.product_cats.category2 && cat.cat_level > 3) {
      display += `> ${cat.product_cats.category2} `;
    }
    if (cat.product_cats.category3 && cat.cat_level > 4) {
      display += `> ${cat.product_cats.category3} `;
    }
  }

  const getCatUrl = (
    filters: PageSideFilters,
    current_cat: CategoryDocument
  ) => {
    if (!pathname) return '';

    let base_path = current_cat.full_slug.split('/').slice(0, -1).join('/');
    let end_path = current_cat.full_slug.split('/').slice(-1)[0];

    const urlAdditions = [];
    if (filters.conditions && filters.conditions.length === 1) {
      // technically conditions can only be 1 cause its a radio but just in case
      let condition = filters.conditions[0];
      urlAdditions.push(condition.toLowerCase());
    }
    if (filters.genders) {
      filters.genders.forEach((gender) => {
        const urlGenders = {
          Y: 'youth',
          W: 'women',
          M: 'men',
          U: 'unisex',
        };
        urlAdditions.push(urlGenders[gender].toLowerCase());
      });
    }
    if (filters.numberSizes) {
      filters.numberSizes.forEach((size) => {
        urlAdditions.push(`size-${size.replace('.', '-')}`);
      });
    }
    if (filters.letterSizes) {
      filters.letterSizes.forEach((size) => {
        urlAdditions.push(`size-${size}`);
      });
    }
    if (filters.bikeYears?.length) {
      filters.bikeYears.forEach((year) => {
        urlAdditions.push(slugify(`${year}`));
      });
    }
    if (filters.bikeMakes?.length) {
      filters.bikeMakes.forEach((make) => {
        urlAdditions.push(slugify(make));
      });
    }
    if (filters.bikeModels) {
      filters.bikeModels.forEach((model) => {
        urlAdditions.push(slugify(model));
      });
    }
    if (!current_cat?.is_master_product && filters.brands?.length) {
      let brand = filters.brands[0];
      if (brand) {
        brand = brand.replace('%', '-percent').replace(' ', '-');
        brand = brand.replace('&', '');
        brand = brand.replace(`'`, '').replace(')', '');
        brand = brand.replace(' ', '-');
        brand = brand.replace('’', '');
        urlAdditions.push(brand.toLowerCase());
      }
    }
    if (filters.onsale) {
      urlAdditions.push('on-sale');
    }
    let path = `${base_path}`;
    let additions_path = urlAdditions.join('-');
    if (additions_path.endsWith('-')) {
      additions_path = additions_path.slice(0, -1);
    }
    if (additions_path.length > 0) {
      if (end_path === 'search') {
        path += `/search/${additions_path}`;
      } else {
        path += `/${additions_path}-${end_path}`;
      }
    } else {
      path += `/${end_path}`;
    }
    if (path.endsWith('-')) {
      path = path.slice(0, -1);
    }
    return path;
  };

  function formatFacetCounts(): {
    hierarchy: {
      mainCats: CategoryFacetCount[] | undefined;
      otherCats: CategoryFacetCount[] | undefined;
    };
  } {
    const cat1s =
      facet_counts?.category1?.map((cat) => ({
        value: cat.value,
        count: cat.count,
        highlighted: cat.highlighted,
        order: 'category1',
      })) ?? [];
    const cat2s =
      facet_counts?.category2?.map((cat) => ({
        value: cat.value,
        count: cat.count,
        highlighted: cat.highlighted,
        order: 'category2',
      })) ?? [];
    const cat3s =
      facet_counts?.category3?.map((cat) => ({
        value: cat.value,
        count: cat.count,
        highlighted: cat.highlighted,
        order: 'category3',
      })) ?? [];

    const hierarchy = {
      mainCats: facet_counts?.category?.map((cat) => ({
        value: cat.value,
        count: cat.count,
        highlighted: cat.highlighted,
        order: 'category',
      })),
      otherCats: [...cat1s, ...cat2s, ...cat3s]
        .sort((a, b) => b.count - a.count)
        .filter(
          (c, i, self) => i === self.findIndex((t) => t.value === c.value)
        ),
    };
    return { hierarchy };
  }

  const formattedFacetCounts = formatFacetCounts();
  const countryCodes = ['US', 'CA', 'MX', 'PR', 'EU', 'AU', 'SAM'];

  const onResetFilters = () => {
    setFilterState({
      ...filterState,
      brands: [],
    });
  };

  return {
    handlePricesChange,
    handleBrandsChange,
    handleSaleChange,
    handleRecentlySoldChange,
    handleGenderChange,
    handleLetterSizeChange,
    handleShipToChange,
    handleShipFromChange,
    handleFreeShippingChange,
    handleCategoryChange,
    handleRideTypeChange,
    getSizeTitle,
    getBrandFilterOptions,
    brandFilter,
    setBrandFilter,
    aspectRatioFilter,
    setAspectRatioFilter,
    getCatUrl,
    formattedFacetCounts,
    startAllClosed,
    setStartAllClosed,
    countryCodes,
    showMobileDrawer,
    setShowMobileDrawer,
    onResetFilters,
    applyPendingFilterChanges,
    pendingFilterChanges,
    handleNumberSizeChange,
    handleAspectRatioChange,
    handleConditionChange,
    handleRimDiameterChange,
    handleMountingPositionChange,
    handleTireDiameterChange,
    handleWidthChange,
    handleConstructionChange,
  };
};
